import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  getExhibitionSettings,
  getLoginTimestamp,
  getSelectedExhibition,
  getSelectedExhibitionId,
  getSelectedStep
} from '@app/app.reducer';
import { AppService } from '@app/app.service';
import { LsButtonComponent, LsModalComponent } from '@axess/ng-limestone';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import {
  getCountdownTime,
  millisecondsBeforeShowingLogoutCountdown,
  secondsToLoginSessionExpiry
} from '@core/utils/datetime-helpers';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BookingTariff, VoucherBookingCountdown } from '@products/models/booking.model';
import { VoucherService } from '@products/services/voucher.service';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { ExhibitionSettingModel } from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import {
  getProductBookingCountdown,
  getVoucherBookingCountdowns
} from '@store/products/booking/countdown/booking-countdown.selectors';
import { getAllBookedTariffs } from '@store/products/booking/product/product-booking.selectors';
import { getBookedVoucherTariffByVoucherCode } from '@store/products/booking/product/voucher/voucher-booking.selectors';
import { getVoucherTariffByVoucherCode } from '@store/products/product-selection/product-selection.selectors';
import { StepName } from '@store/step-forms/step.interface';
import { OverlayPortal } from '@ui/directives/overlay-portal';
import { isNil } from 'lodash';
import { Subscription, combineLatest, filter, first, interval, map, mergeMap, switchMap, timer } from 'rxjs';

@Component({
  selector: 'app-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.scss'],
  standalone: true,
  imports: [TranslateModule, HideTranslationPipe, OverlayPortal, LsButtonComponent, LsModalComponent]
})
export class CountdownComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly appService = inject(AppService);
  private readonly statusBarService = inject(StatusBarService);
  private readonly customizationService = inject(CustomizationService);
  private readonly voucherService = inject(VoucherService);

  timeToReleaseTicket = '';
  nextExpiringVoucher: {
    code: string;
    remaining: number;
    showTime: string;
  };
  ticketBookingTimeoutModalOpen = false;

  protected logoutCountdown = this.setLogoutCountdownSignal();

  private loginInterval;
  private vouchersInterval;
  private ticketInterval;

  private readonly subscription: Subscription = new Subscription();

  ngOnInit() {
    /* PRODUCT COUNTDOWN */
    this.subscription.add(
      combineLatest([
        this.store.select(getAllBookedTariffs),
        this.store.select(getExhibitionSettings),
        this.store.select(getSelectedStep),
        this.store.select(getProductBookingCountdown)
      ])
        .pipe(filter(([allBookedTariffs]) => !!allBookedTariffs.length))
        .subscribe(
          ([_, eventSettings, selectedStep, productBookingCountdown]: [
            BookingTariff[],
            ExhibitionSettingModel,
            string,
            number
          ]) => {
            if (!eventSettings) {
              this.clearTicketInterval();
              return;
            }

            if (this.ticketInterval) {
              clearInterval(this.ticketInterval);
              this.ticketInterval = null;
            }

            if (productBookingCountdown) {
              if (selectedStep === 'invoice') {
                this.clearTicketInterval();
                return;
              }

              this.ticketInterval = setInterval(() => {
                const now = Date.now();
                this.timeToReleaseTicket = getCountdownTime((productBookingCountdown - now) / 1000);

                if (productBookingCountdown < now) {
                  // release all tickets. Bookings are released automatically on BE, so we just reset the reducer without user
                  this.ticketBookingTimeoutModalOpen = true;

                  this.clearTicketInterval();
                }
              }, 1000);
            } else {
              this.clearTicketInterval();
              return;
            }
          }
        )
    );

    /* VOUCHERS COUNTDOWN */
    this.subscription.add(
      this.store
        .select(getVoucherBookingCountdowns)
        .subscribe((voucherBookingCountdowns: VoucherBookingCountdown[]) => {
          clearInterval(this.vouchersInterval);
          this.vouchersInterval = null;

          if (voucherBookingCountdowns.length) {
            // otherwise check if there is interval already, if not, create new one
            this.vouchersInterval = setInterval(() => {
              const now = Date.now();
              const initialValues = {
                code: '',
                remaining: Infinity,
                showTime: ''
              };

              this.nextExpiringVoucher = voucherBookingCountdowns.reduce((result, currVoucher) => {
                // we only release limited vouchers
                const timeWhenEnds = currVoucher.expiryTimestamp;

                if (timeWhenEnds > now) {
                  // if the voucher is still valid get its time
                  if (timeWhenEnds - now < result.remaining) {
                    result.remaining = timeWhenEnds - now;
                    result.code = currVoucher.voucherCode;
                  }

                  return result;
                } else {
                  /* At this time the voucher was released by backend, so we need to remove it from FE as well */
                  combineLatest([
                    this.store.pipe(select(getBookedVoucherTariffByVoucherCode(currVoucher.voucherCode))),
                    this.store.pipe(select(getVoucherTariffByVoucherCode(currVoucher.voucherCode)))
                  ])
                    .pipe(
                      first(),
                      mergeMap(([bookedVoucherTariff, voucherTariff]) => {
                        // now remove holder forms related to this voucher ticket if there is any booked voucher tariff
                        const ticketPersonId = bookedVoucherTariff
                          ? bookedVoucherTariff.ticketPersonId
                          : voucherTariff.ticketPersonId;

                        this.voucherService.releaseExpiredVoucher(ticketPersonId, currVoucher.voucherCode);

                        return combineLatest([
                          this.store.pipe(select(getSelectedExhibition)),
                          this.store.pipe(select(getSelectedStep)),
                          this.store.pipe(select(getAllBookedTariffs))
                        ]).pipe(
                          first(
                            ([selectedExhibition, selectedStep, getAllBookedTariffs]) =>
                              !getAllBookedTariffs.some(
                                (bookedTariff) => bookedTariff.voucherCode === currVoucher.voucherCode
                              ) &&
                              !!selectedExhibition &&
                              !!selectedStep
                          ),
                          mergeMap(([selectedExhibition, selectedStep, allBookedTariffs]) => {
                            if (!allBookedTariffs.length && selectedStep !== StepName.Tickets) {
                              this.router.navigate([
                                `${BaseRoute.WebShop}/${selectedExhibition.id}/${StepRoute.Tickets}`
                              ]);
                            }

                            return this.translateService.get('voucher.timer-ranOut');
                          })
                        );
                      })
                    )
                    .subscribe((translation) => this.statusBarService.setStatus(translation, 'error'));

                  return result;
                }
              }, initialValues);

              this.nextExpiringVoucher.showTime = getCountdownTime(this.nextExpiringVoucher.remaining / 1000);
            }, 1000);
          } else {
            this.nextExpiringVoucher = null;
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    clearInterval(this.loginInterval);
    clearInterval(this.vouchersInterval);

    this.loginInterval = null;
    this.vouchersInterval = null;
  }

  closeModalWindow($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.ticketBookingTimeoutModalOpen = false;

    this.store
      .pipe(
        select(getSelectedExhibitionId),
        first(),
        switchMap((eventId) => {
          this.customizationService.resetShoppingStartTime();
          return this.appService.resetReducers().pipe(map(() => eventId));
        })
      )
      .subscribe((eventId) => window.location.replace(`${eventId ? `/${BaseRoute.WebShop}/${eventId}` : '/'}`));
  }

  clearTicketInterval() {
    clearInterval(this.ticketInterval);
    this.ticketInterval = null;
    this.timeToReleaseTicket = '';
  }

  private setLogoutCountdownSignal() {
    return toSignal(
      this.store.pipe(
        select(getLoginTimestamp),
        filter((loginTimestamp) => !isNil(loginTimestamp)),
        switchMap((loginTimestamp) =>
          timer(millisecondsBeforeShowingLogoutCountdown(loginTimestamp)).pipe(
            switchMap(() =>
              interval(1000).pipe(map(() => getCountdownTime(secondsToLoginSessionExpiry(loginTimestamp))))
            )
          )
        )
      )
    );
  }
}
